/**
 * GoogleAdsConnectPanel – displayed in the marketing-analyst module
 * Shows connect/disconnect, customer account selection, "Pobierz dane" button, and KPI dashboard.
 * Mirrors the MetaConnectPanel architecture.
 */
import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { resolveDisplayCurrency, formatCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  DollarSign,
  MousePointerClick,
  Target,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Eye,
  RefreshCw,
  Search,
  CheckCircle2,
  XCircle,
  Check,
  AlertTriangle,
} from "lucide-react";

interface CustomerAccount {
  id: string;
  descriptiveName: string;
  currencyCode: string;
}

interface GoogleAdsStatus {
  connected: boolean;
  googleEmail?: string;
  selectedCustomerId?: string;
  selectedCustomerName?: string;
  customerAccounts?: CustomerAccount[];
  tokenExpiresAt?: string;
  /** VP-161: trwała odmowa dostępu do wybranego konta po stronie Google. */
  authError?: { at: string; code: string } | null;
}

interface CampaignInsight {
  campaignId: string;
  campaignName: string;
  status: string;
  impressions: number;
  clicks: number;
  costMicros: number;
  conversions: number;
  conversionsValue: number;
  ctr: number;
  avgCpc: number;
  costPerConversion: number;
}

interface GoogleAdsInsightsData {
  campaigns: CampaignInsight[];
  adGroups: unknown[];
  context: string;
  customerId: string;
  customerName: string;
  datePreset: string;
}

interface GoogleAdsConnectPanelProps {
  onInsightsLoaded?: (context: string) => void;
  forUserId?: string;
}

function buildDatePresets(t: TFunction) {
  return [
    { value: "today", label: t("datePresets.today") },
    { value: "yesterday", label: t("datePresets.yesterday") },
    { value: "last_7d", label: t("datePresets.last_7d") },
    { value: "last_14d", label: t("datePresets.last_14d") },
    { value: "last_30d", label: t("datePresets.last_30d") },
    { value: "last_90d", label: t("datePresets.last_90d") },
    { value: "this_month", label: t("datePresets.this_month") },
    { value: "last_month", label: t("datePresets.last_month") },
  ];
}

// ---- Manual Customer ID input with auto-validation ----
function ManualCustomerIdInput({
  onSave,
  isPending,
}: {
  onSave: (id: string, name: string) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation("googleAdsPanel");
  const [customerId, setCustomerId] = useState("");
  const [validation, setValidation] = useState<{
    valid?: boolean;
    descriptiveName?: string;
    currencyCode?: string;
    error?: string;
  } | null>(null);

  const validateMutation = useMutation({
    mutationFn: (cid: string) =>
      apiRequest<{
        valid?: boolean | undefined;
        descriptiveName?: string | undefined;
        currencyCode?: string | undefined;
        error?: string | undefined;
      }>("POST", "/api/integrations/google-ads/validate-customer", { customerId: cid }),
    onSuccess: (data: {
      valid?: boolean;
      descriptiveName?: string;
      currencyCode?: string;
      error?: string;
    }) => setValidation(data),
  });

  const handleValidate = () => {
    const cleaned = customerId.replace(/[-\s]/g, "");
    if (cleaned.length >= 7) {
      setValidation(null);
      validateMutation.mutate(cleaned);
    }
  };

  const handleSave = () => {
    const cleaned = customerId.replace(/[-\s]/g, "");
    if (validation?.valid) {
      onSave(cleaned, validation.descriptiveName || t("manual.accountFallback", { id: cleaned }));
    }
  };

  return (
    <div className="space-y-2 p-2.5 bg-white/[0.03] rounded-lg border border-white/[0.08]">
      <p className="text-xs text-white/50">{t("manual.hint")}</p>
      <div className="flex items-center gap-2">
        <Input
          placeholder={t("manual.placeholder")}
          value={customerId}
          onChange={(e) => {
            setCustomerId(e.target.value);
            setValidation(null);
          }}
          onKeyDown={(e) => e.key === "Enter" && handleValidate()}
          className="h-8 text-xs bg-[#030407] border-white/[0.1] text-white flex-1"
        />
        {!validation?.valid ? (
          <Button
            size="sm"
            onClick={handleValidate}
            disabled={validateMutation.isPending || customerId.replace(/[-\s]/g, "").length < 7}
            className="h-8 text-xs bg-[#4285F4] hover:bg-[#3b78e7] text-white"
          >
            {validateMutation.isPending ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin mr-1" /> {t("manual.checking")}
              </>
            ) : (
              <>
                <Search className="w-3 h-3 mr-1" /> {t("manual.check")}
              </>
            )}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isPending}
            className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white"
          >
            {isPending ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <CheckCircle2 className="w-3 h-3 mr-1" />
            )}
            {t("manual.connectAccount")}
          </Button>
        )}
      </div>

      {/* Validation result */}
      {validation && (
        <div
          className={`flex items-center gap-2 p-2 rounded text-xs ${
            validation.valid
              ? "bg-green-500/10 border border-green-500/30 text-green-400"
              : "bg-red-500/10 border border-red-500/30 text-red-400"
          }`}
        >
          {validation.valid ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                <strong>{validation.descriptiveName}</strong> ({customerId.replace(/[-\s]/g, "")}) –{" "}
                {validation.currencyCode}
              </span>
            </>
          ) : (
            <>
              <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{validation.error}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("pl-PL").format(value);
}

// ---- KPI Card ----
function KpiCard({
  label,
  value,
  icon: Icon,
  color,
  subtitle,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
}) {
  const colorMap: Record<string, { bg: string; text: string; border: string; iconBg: string }> = {
    blue: {
      bg: "bg-blue-500/10",
      text: "text-blue-400",
      border: "border-blue-500/20",
      iconBg: "bg-blue-500/20",
    },
    green: {
      bg: "bg-emerald-500/10",
      text: "text-emerald-400",
      border: "border-emerald-500/20",
      iconBg: "bg-emerald-500/20",
    },
    amber: {
      bg: "bg-amber-500/10",
      text: "text-amber-400",
      border: "border-amber-500/20",
      iconBg: "bg-amber-500/20",
    },
    rose: {
      bg: "bg-rose-500/10",
      text: "text-rose-400",
      border: "border-rose-500/20",
      iconBg: "bg-rose-500/20",
    },
    cyan: {
      bg: "bg-cyan-500/10",
      text: "text-cyan-400",
      border: "border-cyan-500/20",
      iconBg: "bg-cyan-500/20",
    },
    red: {
      bg: "bg-red-500/10",
      text: "text-red-400",
      border: "border-red-500/20",
      iconBg: "bg-red-500/20",
    },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div className={`${c.bg} border ${c.border} rounded-xl p-3 flex flex-col gap-1`}>
      <div className="flex items-center gap-2">
        <div className={`${c.iconBg} rounded-lg p-1.5`}>
          <Icon className={`h-3.5 w-3.5 ${c.text}`} />
        </div>
        <span className="text-[11px] text-white/60 font-medium uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className={`text-xl font-bold ${c.text} mt-1`}>{value}</div>
      {subtitle && <span className="text-[10px] text-white/40">{subtitle}</span>}
    </div>
  );
}

// ---- KPI Dashboard for Google Ads ----
function GoogleAdsKpiDashboard({
  campaigns,
  currency,
}: {
  campaigns: CampaignInsight[];
  currency: string;
}) {
  const { t } = useTranslation("googleAdsPanel");
  const [showCampaigns, setShowCampaigns] = useState(false);

  const totalSpend = campaigns.reduce((s, c) => s + c.costMicros, 0) / 1_000_000;
  const totalClicks = campaigns.reduce((s, c) => s + c.clicks, 0);
  const totalImpressions = campaigns.reduce((s, c) => s + c.impressions, 0);
  const totalConversions = campaigns.reduce((s, c) => s + c.conversions, 0);
  const totalConvValue = campaigns.reduce((s, c) => s + c.conversionsValue, 0);
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
  const avgCpc = totalClicks > 0 ? totalSpend / totalClicks : 0;
  const costPerConversion = totalConversions > 0 ? totalSpend / totalConversions : 0;
  const roas = totalSpend > 0 ? totalConvValue / totalSpend : 0;

  return (
    <div className="space-y-3 mt-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <KpiCard
          label={t("kpi.spend")}
          value={formatCurrency(totalSpend, currency)}
          icon={DollarSign}
          color="blue"
          subtitle={t("kpi.spendSubtitle")}
        />
        <KpiCard
          label={t("kpi.clicks")}
          value={formatNumber(totalClicks)}
          icon={MousePointerClick}
          color="green"
          subtitle={t("kpi.clicksSubtitle", {
            ctr: avgCtr.toFixed(2),
            cpc: formatCurrency(avgCpc, currency),
          })}
        />
        <KpiCard
          label={t("kpi.conversions")}
          value={totalConversions.toFixed(1)}
          icon={Target}
          color="amber"
          subtitle={
            totalConversions > 0
              ? t("kpi.conversionsCost", { cost: formatCurrency(costPerConversion, currency) })
              : t("kpi.noConversions")
          }
        />
        <KpiCard
          label={t("kpi.roas")}
          value={roas > 0 ? `${roas.toFixed(2)}x` : "—"}
          icon={TrendingUp}
          color={roas >= 1 ? "green" : "rose"}
          subtitle={
            totalConvValue > 0
              ? t("kpi.conversionValue", { value: formatCurrency(totalConvValue, currency) })
              : t("kpi.noData")
          }
        />
      </div>

      {/* Campaign Breakdown Toggle */}
      {campaigns.length > 0 && (
        <div>
          <button
            onClick={() => setShowCampaigns(!showCampaigns)}
            className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white/70 transition-colors w-full"
          >
            {showCampaigns ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            <span className="font-medium">
              {t("kpi.campaignBreakdown", { count: campaigns.length })}
            </span>
          </button>

          {showCampaigns && (
            <div className="mt-2 rounded-lg border border-white/[0.08] overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#0a0c10]/80 text-white/60">
                    <th className="text-left p-2 font-medium">{t("kpi.colCampaign")}</th>
                    <th className="text-right p-2 font-medium">{t("kpi.colSpend")}</th>
                    <th className="text-right p-2 font-medium">{t("kpi.colClicks")}</th>
                    <th className="text-right p-2 font-medium">{t("kpi.colCtr")}</th>
                    <th className="text-right p-2 font-medium">{t("kpi.colConversions")}</th>
                    <th className="text-right p-2 font-medium">{t("kpi.colCostPerConv")}</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => {
                    const spend = c.costMicros / 1_000_000;
                    return (
                      <tr
                        key={c.campaignId}
                        className="border-t border-white/[0.06] hover:bg-[#0a0c10]/40 transition-colors"
                      >
                        <td
                          className="p-2 text-white font-medium max-w-[200px] truncate"
                          title={c.campaignName}
                        >
                          {c.campaignName}
                        </td>
                        <td className="p-2 text-right text-blue-400">
                          {formatCurrency(spend, currency)}
                        </td>
                        <td className="p-2 text-right text-emerald-400">
                          {formatNumber(c.clicks)}
                        </td>
                        <td className="p-2 text-right text-white/70">
                          {(c.ctr * 100).toFixed(2)}%
                        </td>
                        <td className="p-2 text-right text-amber-400">
                          {c.conversions > 0 ? c.conversions.toFixed(1) : "—"}
                        </td>
                        <td className="p-2 text-right text-rose-400">
                          {c.conversions > 0 ? formatCurrency(c.costPerConversion, currency) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-white/[0.1] bg-[#0a0c10]/60 font-semibold">
                    <td className="p-2 text-white/70">{t("kpi.total")}</td>
                    <td className="p-2 text-right text-blue-300">
                      {formatCurrency(totalSpend, currency)}
                    </td>
                    <td className="p-2 text-right text-emerald-300">{formatNumber(totalClicks)}</td>
                    <td className="p-2 text-right text-white/50">{avgCtr.toFixed(2)}%</td>
                    <td className="p-2 text-right text-amber-300">
                      {totalConversions > 0 ? totalConversions.toFixed(1) : "—"}
                    </td>
                    <td className="p-2 text-right text-rose-300">
                      {totalConversions > 0 ? formatCurrency(costPerConversion, currency) : "—"}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Google Ads Logo ----
const GoogleAdsLogo = () => (
  <div className="w-6 h-6 rounded bg-[#4285F4] flex items-center justify-center">
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
      <path
        d="M3.272 13.446l6.104-10.594c.95-1.647 3.059-2.21 4.706-1.26l.002.001c1.647.95 2.21 3.059 1.26 4.706L9.24 16.893c-.95 1.647-3.059 2.21-4.706 1.26h-.001c-1.647-.95-2.21-3.059-1.26-4.706z"
        fill="#FBBC04"
      />
      <path
        d="M14.656 13.446l6.104-10.594c.95-1.647-.387-2.21-2.034-1.26l-.002.001-6.104 10.594c-.95 1.647.387 2.21 2.034 1.26h.001z"
        fill="#4285F4"
      />
      <circle cx="6" cy="18.5" r="3.5" fill="#34A853" />
    </svg>
  </div>
);

// ---- Multi-select account selector ----
function GoogleAdsAccountMultiSelect({
  accounts,
  selectedIds,
  onSelectionChange,
  isPending,
  onRefresh,
  isRefreshing,
}: {
  accounts: CustomerAccount[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  isPending: boolean;
  onRefresh: () => void;
  isRefreshing: boolean;
}) {
  const { t } = useTranslation("googleAdsPanel");
  const [open, setOpen] = useState(false);
  const [localIds, setLocalIds] = useState<string[]>(selectedIds);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalIds(selectedIds);
  }, [selectedIds]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (id: string) => {
    setLocalIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      onSelectionChange(next);
      return next;
    });
  };

  const selectedCount = localIds.length;
  const label =
    selectedCount === 0
      ? t("accounts.placeholder")
      : selectedCount === 1
        ? accounts.find((a) => a.id === localIds[0])?.descriptiveName || localIds[0]
        : t("accounts.selected", { count: selectedCount });

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-white/60 whitespace-nowrap">{t("accounts.label")}</span>
      <div ref={ref} className="relative flex-1">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="h-8 w-full flex items-center justify-between px-3 text-xs bg-[#030407] border border-white/[0.1] text-white rounded-md hover:border-white/[0.2] transition-colors"
        >
          <span className="truncate">{label}</span>
          <ChevronDown
            className={`h-3.5 w-3.5 ml-2 text-white/40 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-[#0a0c10] border border-white/[0.12] rounded-lg shadow-xl overflow-hidden">
            <div className="max-h-48 overflow-y-auto">
              {accounts.map((a) => {
                const isSelected = localIds.includes(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => toggle(a.id)}
                    disabled={isPending}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-white/[0.05] transition-colors ${isSelected ? "bg-white/[0.03]" : ""}`}
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-[#4285F4] border-[#4285F4]" : "border-white/20"}`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="text-white truncate">{a.descriptiveName}</span>
                    <span className="text-white/40 ml-auto flex-shrink-0">
                      ({a.id}) – {a.currencyCode}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={onRefresh}
        disabled={isRefreshing}
        className="h-8 px-2 text-xs text-white/50 hover:text-white/70"
        title={t("accounts.refresh")}
      >
        <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
      </Button>
    </div>
  );
}

export default function GoogleAdsConnectPanel({
  onInsightsLoaded,
  forUserId,
}: GoogleAdsConnectPanelProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useTranslation("googleAdsPanel");
  const [datePreset, setDatePreset] = useState("last_30d");
  const datePresets = useMemo(() => buildDatePresets(t), [t]);
  const [insightsData, setInsightsData] = useState<GoogleAdsInsightsData | null>(null);
  const fuParam = forUserId ? `?forUserId=${forUserId}` : "";

  // Check connection status
  const { data: status, isLoading: statusLoading } = useQuery<GoogleAdsStatus>({
    queryKey: ["google-ads-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/google-ads/status${fuParam}`),
    staleTime: 30_000,
  });

  // Listen for postMessage from OAuth popup callback
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "GOOGLE_ADS_OAUTH_SUCCESS") {
        queryClient.invalidateQueries({ queryKey: ["google-ads-integration-status"] });
      }
      if (event.data?.type === "GOOGLE_ADS_OAUTH_ERROR") {
        // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
        console.error("[Google Ads] OAuth error:", event.data.error);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [queryClient]);

  // Connect mutation
  const connectMutation = useMutation({
    mutationFn: () => apiRequest<{ url: string }>("GET", "/api/integrations/google-ads/connect"),
    onSuccess: (data: { url: string }) => {
      const w = 600,
        h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(
        data.url,
        "google_ads_oauth",
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
      );
      const checkInterval = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkInterval);
          queryClient.invalidateQueries({ queryKey: ["google-ads-integration-status"] });
        }
      }, 500);
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/google-ads/disconnect"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["google-ads-integration-status"] });
      setInsightsData(null);
    },
  });

  // Select customer account mutation
  const selectAccountMutation = useMutation({
    mutationFn: (acc: { customerId: string; customerName: string }) =>
      apiRequest("POST", "/api/integrations/google-ads/select-account", acc),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["google-ads-integration-status"] });
    },
  });

  // Refresh customer accounts list from Google Ads API
  const refreshAccountsMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/google-ads/refresh-accounts"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["google-ads-integration-status"] });
    },
  });

  // Fetch insights mutation
  const insightsMutation = useMutation({
    mutationFn: (preset: string) =>
      apiRequest<GoogleAdsInsightsData>("POST", "/api/integrations/google-ads/insights", {
        datePreset: preset,
      }),
    onSuccess: (data: GoogleAdsInsightsData) => {
      setInsightsData(data);
      onInsightsLoaded?.(data.context);
    },
  });

  const selectedIds = status?.selectedCustomerId ? status.selectedCustomerId.split(",") : [];
  const accountCurrency =
    (selectedIds.length > 0
      ? status?.customerAccounts?.find((a) => a.id === selectedIds[0])?.currencyCode
      : status?.customerAccounts?.[0]?.currencyCode) || "PLN";
  const selectedCurrency = resolveDisplayCurrency(user?.preferredCurrency, accountCurrency);

  if (statusLoading) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
        <span className="text-sm text-white/60">{t("checkingConnection")}</span>
      </div>
    );
  }

  // ----- NOT CONNECTED -----
  if (!status?.connected) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GoogleAdsLogo />
            <span className="text-sm font-medium text-white">Google Ads</span>
            <Badge variant="outline" className="text-xs border-white/[0.1] text-white/60">
              {t("notConnected")}
            </Badge>
          </div>
          <Button
            size="sm"
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending}
            className="bg-[#4285F4] hover:bg-[#3367D6] text-white text-xs"
          >
            {connectMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("connecting")}
              </>
            ) : (
              t("connect")
            )}
          </Button>
        </div>
        <p className="text-xs text-white/40">{t("connectDesc")}</p>
      </div>
    );
  }

  // ----- CONNECTED -----
  const accounts = status.customerAccounts || [];

  return (
    <div className="p-3 bg-white/[0.04] border border-[#4285F4]/30 rounded-lg space-y-3">
      {/* Header: connection status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GoogleAdsLogo />
          <span className="text-sm font-medium text-white">Google Ads</span>
          <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
            {t("connected", { email: status.googleEmail })}
          </Badge>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => disconnectMutation.mutate()}
          disabled={disconnectMutation.isPending}
          className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          {t("disconnect")}
        </Button>
      </div>

      {/* VP-161: Google odmawia dostępu do wybranego konta (wyłączone, dezaktywowane,
          cofnięte uprawnienia). Bez tego pasek na górze mówi „Połączono", a dane po
          prostu nie przychodzą — użytkownik nie ma jak się domyślić dlaczego. */}
      {status.authError && (
        <div className="flex items-start gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-amber-400">{t("accountUnavailableTitle")}</p>
            <p className="text-xs text-white/60">
              {t("accountUnavailableBody", { code: status.authError.code })}
            </p>
          </div>
        </div>
      )}

      {/* Account selector — multi-select with checkboxes */}
      {accounts.length >= 1 && (
        <GoogleAdsAccountMultiSelect
          accounts={accounts}
          selectedIds={status.selectedCustomerId ? status.selectedCustomerId.split(",") : []}
          onSelectionChange={(ids) => {
            const names = ids.map((id) => accounts.find((a) => a.id === id)?.descriptiveName || id);
            selectAccountMutation.mutate({
              customerId: ids.join(","),
              customerName: names.join(", "),
            });
          }}
          isPending={selectAccountMutation.isPending}
          onRefresh={() => refreshAccountsMutation.mutate()}
          isRefreshing={refreshAccountsMutation.isPending}
        />
      )}

      {accounts.length === 0 && !status.selectedCustomerId && (
        <ManualCustomerIdInput
          onSave={(id, name) =>
            selectAccountMutation.mutate({ customerId: id, customerName: name })
          }
          isPending={selectAccountMutation.isPending}
        />
      )}

      {accounts.length === 0 && status.selectedCustomerId && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/60">
            {t("accountLabel", { name: status.selectedCustomerName || status.selectedCustomerId })}
          </span>
          <button
            className="text-xs text-blue-400 hover:text-blue-300 underline"
            onClick={() => selectAccountMutation.mutate({ customerId: "", customerName: "" })}
          >
            {t("change")}
          </button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => refreshAccountsMutation.mutate()}
            disabled={refreshAccountsMutation.isPending}
            className="h-6 px-2 text-[10px] text-white/50 hover:text-white/70"
          >
            <RefreshCw
              className={`h-3 w-3 ${refreshAccountsMutation.isPending ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
      )}

      {/* Fetch controls */}
      {status.selectedCustomerId && (
        <div className="flex items-center gap-2">
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="h-8 text-xs bg-[#030407] border-white/[0.1] text-white w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {datePresets.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={() => insightsMutation.mutate(datePreset)}
            disabled={insightsMutation.isPending}
            className="bg-[#4285F4] hover:bg-[#3367D6] text-white text-xs"
          >
            {insightsMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("loadingData")}
              </>
            ) : (
              t("fetchData")
            )}
          </Button>
        </div>
      )}

      {/* Results */}
      {insightsData && insightsData.campaigns.length > 0 && (
        <>
          <GoogleAdsKpiDashboard campaigns={insightsData.campaigns} currency={selectedCurrency} />
          <div className="flex items-center gap-2">
            <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
              {t("dataLoaded")}
            </Badge>
            <span className="text-xs text-white/40">{t("dataLoadedDesc")}</span>
          </div>
        </>
      )}

      {insightsData && insightsData.campaigns.length === 0 && (
        <div className="text-center py-4">
          <p className="text-xs text-white/40">{t("noCampaignsData")}</p>
        </div>
      )}

      {insightsMutation.isError && (
        <div className="p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
          {t("fetchError")}
        </div>
      )}
    </div>
  );
}
